import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { rests } from '@core/util/rests';
import { HttpFormParams } from '@core/services/http-form-params';
import { CompanyResponse } from '@models/entities/partner';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { Company, ExtendedCompany, Owner, PartnerListResponse, OfficeListByMasterFranchise } from '@models/partners/company';
import { CommonFilter, CommonFilterWithPaginator } from '@models/filters/common-filter-with-paginator';
import { prepareFilter } from '@core/util/prepareFilter';
import { CompanyTariffResponse } from '@models/companyTariff';
import { FranchiseRoyaltiesResponse, Share } from '@shared/interfaces/share';
import { List } from '@shared/interfaces/list';
import { concatMap, map } from 'rxjs/operators';
import { allowedCountries } from '@core/country/allowed-countries';
import { AllowedCountry } from '@models/allowedCountry';
import { ContentResponse } from '@models/response';
import { CompanyBilling } from '@models/billing/company-billing';
import { Permissions } from '@core/util/permissions.enum';
import { AppContextService } from '@core/services/app-context.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  shopCompanyData: BehaviorSubject<CompanyResponse> = new BehaviorSubject(null);
  filialList: BehaviorSubject<Partial<ExtendedCompany>[]> = new BehaviorSubject(null);
  filialMap: BehaviorSubject<Map<number, ExtendedCompany>> = new BehaviorSubject(null);
  getCompany: Subject<number> = new Subject();
  permissions = Permissions;

  constructor(private http: HttpClient, private appContext: AppContextService) {}

  shopCompanyList(filter): Observable<PartnerListResponse> {
    const params = prepareFilter(filter);
    return this.http.get<PartnerListResponse>(rests.company.list, { params });
  }

  createShopCompany(company, companyTariffPlan, owner): Observable<ExtendedCompany> {
    const data = { company, owner };

    if (companyTariffPlan) {
      data['companyTariffPlan'] = companyTariffPlan;
    }

    const params = HttpFormParams.settingByKeys(data);
    return this.http.post<ExtendedCompany>(rests.company.create, params);
  }

  editShopCompany(id, company: ExtendedCompany): Observable<any> {
    return this.http.post(rests.company.edit(id), company);
  }

  getShopCompany(id): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(rests.company.get_by_id(id));
  }

  activateShopCompany(id, activate) {
    if (activate) {
      return this.http.post(rests.company.activate(id), null);
    } else {
      return this.http.post(rests.company.block(id), null);
    }
  }

  createOfficeCompany(company, owner): Observable<ExtendedCompany> {
    const params = HttpFormParams.settingByKeys({ company, owner });
    return this.http.post<ExtendedCompany>(rests.company.office.create, params);
  }

  createMasterFranchise(office: Company, owner: Owner): Observable<ExtendedCompany> {
    const params = HttpFormParams.settingByKeys({ office, owner });
    return this.http.post<ExtendedCompany>(rests.master_franchise.create, params);
  }

  getOfficeListByMasterfranchise(id: string, filter?: CommonFilter): Observable<OfficeListByMasterFranchise> {
    const params = filter ? prepareFilter(filter) : {};
    return this.http.get<OfficeListByMasterFranchise>(rests.master_franchise.get_offices(id), { params });
  }

  officeCompanyList(filter?: CommonFilter): Observable<List<ExtendedCompany>> {
    const params = filter ? prepareFilter(filter) : {};
    return this.http.get<List<ExtendedCompany>>(rests.company.office.list, { params });
  }

  officeCompany(officeId?, isMaster?, filter?: CommonFilterWithPaginator): Observable<List<ExtendedCompany>> {
    const params = {
      officeId,
      isMaster,
      ...filter,
    };
    return this.http.get<List<ExtendedCompany>>(rests.company.office.list, { params });
  }

  getListCompany(isMaster: boolean): Observable<List<ExtendedCompany>> {
    const params = {
      isMaster,
    };
    return this.http.get<List<ExtendedCompany>>(rests.company.office.list, { params });
  }

  getParentOffice(officeIds: number[]): Observable<List<ExtendedCompany>> {
    const params = {
      officeIds,
    };
    return this.http.get<List<ExtendedCompany>>(rests.company.office.list, { params });
  }

  getFranchises(filter?: any): Observable<List<ExtendedCompany>> {
    delete filter?.deleted;
    const params = filter ? prepareFilter(filter) : {};
    return this.http.get<List<ExtendedCompany>>(rests.company.office.list, { params });
  }

  getMasterFranchises(officeId: number, isMaster: boolean): Observable<List<ExtendedCompany>> {
    const params = {
      officeId,
      isMaster,
    };
    return this.http.get<List<ExtendedCompany>>(rests.company.office.list, { params });
  }

  getFranchise(id: number): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(rests.company.get_by_id(id));
  }

  getOfficeCompany(id): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(rests.company.office.get_by_id(id));
  }

  editOfficeCompany(id, company: ExtendedCompany) {
    return this.http.post(rests.company.office.edit(id), company);
  }

  listByTariff(filter): Observable<CompanyTariffResponse> {
    const preparedFilter = filter ? prepareFilter(filter) : {};
    let params = new HttpParams();
    Object.keys(preparedFilter).forEach((key) => {
      if (key !== 'tariffTypes') {
        params = params.set(key, preparedFilter[key]);
      }
    });
    if (preparedFilter.tariffTypes) {
      const tariffTypesArray = JSON.parse(preparedFilter.tariffTypes);
      tariffTypesArray.forEach((type) => {
        params = params.append('tariffTypes', type);
      });
    }
    return this.http.get<CompanyTariffResponse>(rests.company.list_by_tariff_plan, { params });
  }

  massiveTransfer(toOfficeId: number, companyIds: number[]) {
    const body = {
      franchiseId: toOfficeId,
      companiesIds: companyIds,
    };

    return this.http.post(rests.company.massive_transfer, body);
  }

  officeBlock(officeId) {
    return this.http.post(rests.company.office.block(officeId), null);
  }

  officeUnblock(officeId) {
    return this.http.post(rests.company.office.unblock(officeId), null);
  }

  createShareMasterFranchise(companyId: number, share: Share) {
    const body = {
      royaltyMultiplier: share.value,
      franchiseId: companyId,
      activeFrom: share.activeFrom,
    };
    return this.http.post(rests.company.share.create, body);
  }

  getShareMasterFranchiseList(companyId: number): Observable<FranchiseRoyaltiesResponse> {
    return this.http.get<FranchiseRoyaltiesResponse>(rests.company.share.list, { params: { companyId } });
  }

  getUmagRoot(): Observable<ExtendedCompany> {
    return this.http.get<any>(rests.company.get_currency).pipe(
      concatMap((data) => {
        const countries: AllowedCountry[] = allowedCountries;
        const result = [];
        data.allowedCurrencies.forEach((el) => {
          countries.find((country) => {
            if (el === country.currencyCode) {
              result.push(country);
            }
          });
        });
        localStorage.setItem('allowedCountry', JSON.stringify(result));
        return this.getRoot(result[0].countryCode).pipe(
          map((root) => {
            return root;
          })
        );
      })
    );
  }

  getRoot(currency: string): Observable<ExtendedCompany> {
    return this.http.get<ExtendedCompany>(rests.company.umag_currency_root(currency));
  }

  getAllowedCountries(): AllowedCountry[] {
    return this.appContext.allowedCountries;
  }

  setFranchise(data) {
    return this.http.post(rests.company.set_franchise, {
      masterFranchiseId: data.masterFranchiseId,
      franchiseId: data.franchiseId,
    });
  }

  setCompany(data) {
    return this.http.post(rests.company.set_company, {
      franchiseId: data.franchiseId,
      companyId: data.companyId,
    });
  }

  getCompaniesBillings() {
    return this.http.get<ContentResponse<CompanyBilling>>(rests.billing.company_reports);
  }

  /* Helpers */
  checkDate(filter, params, key) {
    const timeRgx = /(\d{4}-\d{2}-\d{2}|\d{2}:\d{2})/;
    if (filter[key]) {
      if (timeRgx.test(filter[key])) {
        return params.append(key, new Date(filter[key]).getTime());
      }
    } else {
      return params.delete(key);
    }
  }

  selfUnlocking(companyId: number, enabled?: boolean): Observable<boolean> {
    const url: string = rests.company.self_unlocking(companyId);
    return enabled !== undefined ? this.http.put<boolean>(url, { enabled }) : this.http.get<boolean>(url);
  }
}
